import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useMigrationProgress } from './useMigrationProgress';

interface UploadProgress {
  sequenceId: string;
  progress: number;
  status: 'idle' | 'uploading' | 'success' | 'error';
  error?: string;
}

export function useSequenceUpload() {
  const { toast } = useToast();
  const { updateProgress } = useMigrationProgress();
  const [uploadStatus, setUploadStatus] = useState<Record<string, UploadProgress>>({});

  const uploadSequenceFile = async (
    file: File,
    sequenceId: string,
    userId: string,
    oldStoragePath: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      // Initialize progress tracking
      setUploadStatus(prev => ({
        ...prev,
        [sequenceId]: { sequenceId, progress: 0, status: 'uploading' }
      }));

      // Generate unique filename with user ID prefix
      const timestamp = Date.now();
      const safeFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storagePath = `${userId}/${timestamp}-${safeFileName}`;

      // Determine content type
      let contentType = file.type;
      if (!contentType || contentType === 'application/octet-stream') {
        const ext = safeFileName.split('.').pop()?.toLowerCase();
        if (ext === 'zip') contentType = 'application/zip';
        else if (ext === 'xml') contentType = 'text/xml';
        else if (ext === 'lms') contentType = 'application/x-lms';
        else if (ext === 'fseq') contentType = 'application/x-fseq';
        else if (ext === 'seq') contentType = 'application/x-seq';
        else contentType = 'application/octet-stream';
      }

      console.log('Starting migration upload:', { sequenceId, fileName: file.name, storagePath });

      // Update progress to 10%
      setUploadStatus(prev => ({
        ...prev,
        [sequenceId]: { ...prev[sequenceId], progress: 10 }
      }));

      // Get signed upload URL from backend
      const { data: session } = await supabase.auth.getSession();
      const accessToken = session.session?.access_token;

      console.log('Session debug:', {
        hasSession: !!session.session,
        hasAccessToken: !!accessToken,
        tokenLength: accessToken?.length,
        userId: session.session?.user?.id,
        userEmail: session.session?.user?.email
      });

      if (!accessToken) {
        throw new Error('No access token available');
      }

      const signedRes = await fetch('/api/storage/signed-upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify({ fileName: safeFileName, userId })
      });

      if (!signedRes.ok) {
        const errorText = await signedRes.text();
        throw new Error(`Signed upload URL request failed: ${signedRes.status} - ${errorText}`);
      }

      const { path, token } = await signedRes.json();
      console.log('Got signed upload URL, uploading file...');

      // Update progress to 30%
      setUploadStatus(prev => ({
        ...prev,
        [sequenceId]: { ...prev[sequenceId], progress: 30 }
      }));

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('sequences')
        .uploadToSignedUrl(path, token, file, {
          contentType,
          cacheControl: '3600'
        });

      if (uploadError) {
        throw new Error(`Upload to signed URL failed: ${uploadError.message}`);
      }

      console.log('File uploaded successfully to storage:', path);

      // Update progress to 70%
      setUploadStatus(prev => ({
        ...prev,
        [sequenceId]: { ...prev[sequenceId], progress: 70 }
      }));

      // Update sequences table: move old path to legacy_storage_url, set new path
      const { error: updateError } = await supabase
        .from('sequences')
        .update({
          storage_path: path,
          file_url: null, // Clear old file_url
          legacy_storage_url: oldStoragePath,
          original_filename: file.name,
          content_type: contentType,
          file_size: file.size
        })
        .eq('id', sequenceId);

      if (updateError) {
        throw new Error(`Database update failed: ${updateError.message}`);
      }

      console.log('Database updated successfully');

      // Update progress to 90%
      setUploadStatus(prev => ({
        ...prev,
        [sequenceId]: { ...prev[sequenceId], progress: 90 }
      }));

      // Update migration_progress to complete using React Query mutation
      updateProgress({
        sequenceId,
        status: 'complete',
        notes: `Uploaded: ${file.name}`
      });

      // Success!
      setUploadStatus(prev => ({
        ...prev,
        [sequenceId]: { sequenceId, progress: 100, status: 'success' }
      }));

      toast({
        title: 'Upload successful',
        description: `${file.name} uploaded and database updated`,
      });

      return { success: true };

    } catch (error: any) {
      console.error('Upload error:', error);

      setUploadStatus(prev => ({
        ...prev,
        [sequenceId]: {
          sequenceId,
          progress: 0,
          status: 'error',
          error: error.message
        }
      }));

      toast({
        title: 'Upload failed',
        description: error.message,
        variant: 'destructive',
      });

      return { success: false, error: error.message };
    }
  };

  const markAsSkipped = async (sequenceId: string, notes?: string) => {
    try {
      const { error } = await supabase
        .from('migration_progress')
        .upsert({
          sequence_id: sequenceId,
          status: 'skipped',
          notes,
          uploaded_by: (await supabase.auth.getUser()).data.user?.id
        }, {
          onConflict: 'sequence_id'
        });

      if (error) throw error;

      toast({
        title: 'Marked as skipped',
        description: notes || 'Sequence marked as skipped',
      });

      return { success: true };
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      return { success: false, error: error.message };
    }
  };

  const resetUploadStatus = (sequenceId: string) => {
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[sequenceId];
      return newStatus;
    });
  };

  return {
    uploadSequenceFile,
    markAsSkipped,
    uploadStatus,
    resetUploadStatus,
  };
}
